import apiService from './api';

class AuthService {
  // Check authentication status with short-term caching
  async checkAuthStatus() {
    try {
      const data = await apiService.get('/api/auth/status', {
        cacheTimeout: 30000, // Cache auth status for 30 seconds only
        timeoutMs: 10000 // Fail fast if server is slow
      });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Validate token (no caching as this is security-sensitive)
  async validateToken() {
    try {
      const data = await apiService.get('/api/auth/validate-token', {
        useCache: false, // Never cache token validation
        timeoutMs: 10000
      });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Login user (no caching)
  async login(email, password) {
    try {
      const data = await apiService.post(
        '/api/auth/login',
        { email, password },
        { allowUnauthorized: true }
      );
      // Clear auth-related caches on successful login
      apiService.clearCache('GET:/api/auth/status');
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        ...(error.error_type ? { error_type: error.error_type } : {}),
      };
    }
  }

  // Register user (no caching)
  async register(email, password, username) {
    try {
      const data = await apiService.post('/api/auth/register', {
        email,
        password,
        username,
      });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Logout user (no caching)
  async logout() {
    try {
      await apiService.post('/api/auth/logout');
      // Clear all caches on logout
      apiService.clearCache();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Forgot password (no caching)
  async forgotPassword(email) {
    try {
      const redirectTo =
        typeof window !== 'undefined' && window.location?.origin
          ? `${window.location.origin.replace(/\/$/, '')}/reset-password?token=`
          : undefined;
      const payload = redirectTo ? { email, redirect_to: redirectTo } : { email };
      const data = await apiService.post('/api/auth/forgot-password', payload);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Reset password (no caching)
  async resetPassword(token, newPassword) {
    try {
      const data = await apiService.post('/api/auth/reset-password', {
        token,
        newPassword,
      });
      // Clear auth-related caches after password reset
      apiService.clearCache('GET:/api/auth/status');
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        ...(error.code ? { code: error.code } : {}),
      };
    }
  }

  /** First password for OAuth-only accounts; response includes new access_token. */
  async setInitialPassword(newPassword) {
    try {
      const data = await apiService.post(
        '/api/auth/set-initial-password',
        { newPassword },
        { useCache: false, allowUnauthorized: true }
      );
      apiService.clearCache('GET:/api/auth/status');
      apiService.clearCache('GET:/api/user/settings');
      apiService.clearCache('GET:/api/user/profile');
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /** Authenticated password change; response includes new access_token. */
  async changePassword(currentPassword, newPassword) {
    try {
      const data = await apiService.post(
        '/api/auth/change-password',
        { currentPassword, newPassword },
        { useCache: false, allowUnauthorized: true }
      );
      apiService.clearCache('GET:/api/auth/status');
      apiService.clearCache('GET:/api/user/settings');
      apiService.clearCache('GET:/api/user/profile');
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /** Sync OAuth user with backend after Google authentication */
  async syncOAuthUser(accessToken) {
    try {
      const data = await apiService.post(
        '/api/auth/sync-oauth-user',
        {},
        { 
          useCache: false,
          allowUnauthorized: true,
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );
      // Clear auth-related caches on successful OAuth sync
      apiService.clearCache('GET:/api/auth/status');
      return { success: true, user: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

export default new AuthService();
